/**
 * The inspector: describes and edits what is selected.
 *
 * - Nothing selected: an overview (base types, most used libraries, nodes per target,
 *   languages).
 * - A node: its kind, targets, language, description and code; its inheritance chain and
 *   neighbours; its group; comments; a form to add links; delete.
 * - A link: its kind, direction, ends and label.
 * - A group: its name, parent group, note, the nodes and groups inside it; delete.
 * - A bundle (links between two groups): every link it stands for.
 *
 * Each panel, and each section of the node panel, is its own file in this folder.
 */
import { Match, Switch, createMemo, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { BundlePanel } from "./BundlePanel";
import { GroupPanel } from "./GroupPanel";
import { LinkPanel } from "./LinkPanel";
import { NodePanel } from "./NodePanel";
import { NodesPanel } from "./NodesPanel";
import { OverviewPanel } from "./OverviewPanel";
import { sameEdge } from "../../client/graph";

/** The inspector window. */
export function InspectorWindow(): JSX.Element {
  const { view, graph } = useApp();
  const node = createMemo(() => {
    const s = view.state.selection;
    return s?.type === "node" ? graph.state.graph.nodes.find((n) => n.id === s.id) : undefined;
  });
  const link = createMemo(() => {
    const s = view.state.selection;
    return s?.type === "link" ? graph.state.graph.links.find((l) => sameEdge(l, s.link)) : undefined;
  });
  const group = createMemo(() => {
    const s = view.state.selection;
    return s?.type === "group" ? graph.state.graph.groups.find((g) => g.id === s.id) : undefined;
  });
  const nodeIds = () => {
    const s = view.state.selection;
    return s?.type === "nodes" ? s.ids : undefined;
  };
  const bundleKey = () => {
    const s = view.state.selection;
    return s?.type === "bundle" ? s.key : undefined;
  };
  return (
    <div class="inspector" aria-live="polite">
      <Switch fallback={<OverviewPanel />}>
        <Match when={node()} keyed>
          {(n) => <NodePanel id={n.id} />}
        </Match>
        <Match when={nodeIds()}>{(ids) => <NodesPanel ids={ids()} />}</Match>
        <Match when={link()}>{(l) => <LinkPanel link={l()} />}</Match>
        <Match when={bundleKey()} keyed>
          {(key) => <BundlePanel bundleKey={key} />}
        </Match>
        <Match when={group()} keyed>
          {(g) => <GroupPanel id={g.id} />}
        </Match>
      </Switch>
    </div>
  );
}
